import { USER_ROLES } from './constant';
import { parseModuleOperations } from './voicePlanAcl';
import { getUser } from './setting';

export const AGENT_LEADS_ACL_MODULE = 'Agent Leads';
export const CUSTOM_LEADS_ACL_OPTION = 'custom_leads';
export const DEFAULT_AGENT_LEADS_EDIT_OPTIONS = [CUSTOM_LEADS_ACL_OPTION];
export const DEFAULT_AGENT_LEADS = 100;
export const MAX_AGENT_LEADS = 1000000;

const CALL_CENTRE_USER_ROLES = [
  USER_ROLES.CALL_CENTRE_ADMIN,
  USER_ROLES.CALL_CENTRE_TEAM_LEAD,
  USER_ROLES.CALL_CENTRE_AGENT,
];

/** Add, edit, and delete of lead assignments. */
const AGENT_LEADS_MUTATOR_ROLES = [
  USER_ROLES.SUPERADMIN,
  USER_ROLES.CALL_CENTRE_ADMIN,
];

/** Super admin, sub super admin, and reseller can grant the module, including on call centre default ACLs. */
const AGENT_LEADS_ACL_GRANTOR_ROLES = [
  USER_ROLES.SUPERADMIN,
  USER_ROLES.SUB_SUPERADMIN,
  USER_ROLES.RESELLER,
];

export function isCallCentreUserRole(role?: string | null) {
  return !!role && CALL_CENTRE_USER_ROLES.includes(role);
}

export function parseAgentLeadsInput(value: unknown): { ok: true; value: number } | { ok: false; message: string } {
  if (value === undefined || value === null || String(value).trim() === '') {
    return { ok: false, message: 'Agent leads is required' };
  }
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0 || n > MAX_AGENT_LEADS) {
    return { ok: false, message: `Agent leads must be a whole number from 0 to ${MAX_AGENT_LEADS}` };
  }
  return { ok: true, value: n };
}

export function canMutateAgentLeads(role?: string | null) {
  return !!role && AGENT_LEADS_MUTATOR_ROLES.includes(role);
}

/** Assigning new leads is call centre admin only (backend enforces the same). */
export function canCreateAgentLeads(role?: string | null) {
  return role === USER_ROLES.CALL_CENTRE_ADMIN;
}

/** Grantors may put Agent Leads on any ACL, including call centre, agent, and team lead defaults. */
export function canGrantAgentLeadsAcl(editorRole?: string | null, _defaultForRole?: string | null) {
  return !!editorRole && AGENT_LEADS_ACL_GRANTOR_ROLES.includes(editorRole);
}

type AgentLeadsModuleEntry = {
  read?: boolean;
  add?: boolean;
  edit?: boolean;
  edit_options?: unknown;
} | null | undefined;

type CustomLeadsContext = {
  userRole?: string | null;
  isSuperAdmin?: boolean;
};

type CustomLeadsGrant = {
  canGrantCustomLeads: boolean;
};

function isSuperAdminRole(userRole?: string | null, isSuperAdmin = false) {
  return isSuperAdmin || userRole === USER_ROLES.SUPERADMIN;
}

export function isAgentLeadsCustomRelevant(moduleEntry: AgentLeadsModuleEntry) {
  return !!(moduleEntry && (moduleEntry.read || moduleEntry.add || moduleEntry.edit));
}

export function normalizeAgentLeadsCustomOptions(options: unknown): string[] {
  if (!Array.isArray(options)) return [];
  return [
    ...new Set(
      options
        .map((o) => String(o).trim())
        .filter((o) => o === CUSTOM_LEADS_ACL_OPTION)
    ),
  ];
}

export function hasCustomAgentLeads(
  moduleOperations: unknown,
  { userRole = null, isSuperAdmin = false }: CustomLeadsContext = {}
) {
  if (isSuperAdminRole(userRole, isSuperAdmin)) return true;
  const ops = parseModuleOperations(moduleOperations);
  const entry = ops[AGENT_LEADS_ACL_MODULE];
  if (!isAgentLeadsCustomRelevant(entry)) return false;
  if (!Array.isArray(entry.edit_options)) return true;
  return normalizeAgentLeadsCustomOptions(entry.edit_options).includes(CUSTOM_LEADS_ACL_OPTION);
}

export function getParentGrantableCustomLeads(
  moduleOperations: unknown,
  isSuperAdmin: boolean
): CustomLeadsGrant {
  if (isSuperAdmin) return { canGrantCustomLeads: true };
  return { canGrantCustomLeads: hasCustomAgentLeads(moduleOperations, { isSuperAdmin: false }) };
}

export function normalizeAgentLeadsCustomAclForSave(
  { edit_options = [] }: { edit_options?: unknown } = {},
  parentGrantable: CustomLeadsGrant | null = null
) {
  let options = normalizeAgentLeadsCustomOptions(edit_options);
  if (parentGrantable && !parentGrantable.canGrantCustomLeads) {
    options = [];
  }
  return { edit_options: options };
}

export function formatAgentLeadsCustomAclLabel(moduleEntry: AgentLeadsModuleEntry) {
  if (!isAgentLeadsCustomRelevant(moduleEntry)) return '—';
  if (!moduleEntry || !Array.isArray(moduleEntry.edit_options)) return 'Custom Leads';
  if (!normalizeAgentLeadsCustomOptions(moduleEntry.edit_options).includes(CUSTOM_LEADS_ACL_OPTION)) {
    return '—';
  }
  return 'Custom Leads';
}

function currentUserOps(user = getUser()) {
  const raw = user?.acl_settings?.module_operations ?? user?.module_operations ?? null;
  return {
    ops: parseModuleOperations(raw),
    context: {
      userRole: user?.role || null,
      isSuperAdmin: user?.role === USER_ROLES.SUPERADMIN,
    },
  };
}

export function currentUserHasCustomAgentLeads(user?: { role?: string | null; acl_settings?: { module_operations?: unknown }; module_operations?: unknown } | null) {
  const { ops, context } = currentUserOps(user);
  return hasCustomAgentLeads(ops, context);
}
